import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Edit, Trash2, Users, DollarSign, TrendingUp } from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";

interface Salesperson {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    is_active: boolean;
    total_commission_earned: number;
    total_commission_paid: number;
    notes: string | null;
    created_at: string;
}

export function SalespeopleManager() {
    const [salespeople, setSalespeople] = useState<Salesperson[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const { toast } = useToast();
    const { formatPrice } = useCurrency();
    const { hasPermission } = useAuth();

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        notes: "",
        is_active: true
    });

    useEffect(() => {
        fetchSalespeople();
    }, []);

    const fetchSalespeople = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('salespeople')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) {
            toast({
                title: "Error fetching salespeople",
                description: error.message,
                variant: "destructive"
            });
        } else {
            setSalespeople(data || []);
        }
        setLoading(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.email) {
            toast({
                title: "Validation Error",
                description: "Name and Email are required",
                variant: "destructive"
            });
            return;
        }

        const payload = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            notes: formData.notes || null,
            is_active: formData.is_active
        };

        if (editingId) {
            // Update existing
            const { error } = await supabase
                .from('salespeople')
                .update(payload)
                .eq('id', editingId);

            if (error) {
                toast({ title: "Error updating salesperson", description: error.message, variant: "destructive" });
            } else {
                toast({ title: "Success", description: "Salesperson updated successfully" });
                resetForm();
                fetchSalespeople();
            }
        } else {
            // Create new
            const { error } = await supabase
                .from('salespeople')
                .insert([payload]);

            if (error) {
                toast({ title: "Error creating salesperson", description: error.message, variant: "destructive" });
            } else {
                toast({ title: "Success", description: "Salesperson created successfully" });
                resetForm();
                fetchSalespeople();
            }
        }
    };

    const handleEdit = (person: Salesperson) => {
        setEditingId(person.id);
        setFormData({
            name: person.name,
            email: person.email,
            phone: person.phone || "",
            notes: person.notes || "",
            is_active: person.is_active
        });
        setIsDialogOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure? This will also delete all associated referral codes.")) return;

        const { error } = await supabase
            .from('salespeople')
            .delete()
            .eq('id', id);

        if (error) {
            toast({
                title: "Error",
                description: "Could not delete (may have active referral codes)",
                variant: "destructive"
            });
        } else {
            toast({ title: "Success", description: "Salesperson deleted" });
            fetchSalespeople();
        }
    };

    const toggleStatus = async (id: string, currentStatus: boolean) => {
        const { error } = await supabase
            .from('salespeople')
            .update({ is_active: !currentStatus })
            .eq('id', id);

        if (!error) {
            fetchSalespeople();
            toast({
                title: "Status Updated",
                description: `Salesperson ${!currentStatus ? 'activated' : 'deactivated'}`
            });
        }
    };

    const resetForm = () => {
        setFormData({
            name: "",
            email: "",
            phone: "",
            notes: "",
            is_active: true
        });
        setEditingId(null);
        setIsDialogOpen(false);
    };

    const totalCommissionEarned = salespeople.reduce((sum, p) => sum + (p.total_commission_earned || 0), 0);
    const totalCommissionPaid = salespeople.reduce((sum, p) => sum + (p.total_commission_paid || 0), 0);
    const totalPending = totalCommissionEarned - totalCommissionPaid;

    return (
        <div className="space-y-6">
            {/* Header with Stats */}
            <div>
                <h2 className="text-3xl font-bold tracking-tight">Salespeople</h2>
                <p className="text-muted-foreground">Manage sales agents and track their commissions.</p>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Salespeople</CardTitle>
                        <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{salespeople.length}</div>
                        <p className="text-xs text-muted-foreground">
                            {salespeople.filter(p => p.is_active).length} active
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Earned</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatPrice(totalCommissionEarned)}</div>
                        <p className="text-xs text-muted-foreground">
                            All-time commission
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Pending Payout</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-orange-600">{formatPrice(totalPending)}</div>
                        <p className="text-xs text-muted-foreground">
                            {formatPrice(totalCommissionPaid)} paid
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Action Button */}
            <div className="flex justify-end">
                <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
                    <DialogTrigger asChild>
                        <Button><Plus className="mr-2 h-4 w-4" /> Add Salesperson</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>{editingId ? "Edit Salesperson" : "Add New Salesperson"}</DialogTitle>
                            <DialogDescription>Enter the salesperson's details below.</DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Name *</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="John Smith"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="john@example.com"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone</Label>
                                <Input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="+1 (555) 123-4567"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="notes">Notes</Label>
                                <Textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Additional information..."
                                    rows={3}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={formData.is_active}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                                />
                                <Label>Active</Label>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={resetForm}>Cancel</Button>
                            <Button onClick={handleSubmit}>{editingId ? "Update" : "Create"}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Salespeople List */}
            {loading ? (
                <div className="flex justify-center p-8">
                    <Loader2 className="animate-spin h-8 w-8 text-primary" />
                </div>
            ) : salespeople.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Users className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                            No salespeople yet. Add your first salesperson to start tracking referrals.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {salespeople.map((person) => {
                        const pending = (person.total_commission_earned || 0) - (person.total_commission_paid || 0);
                        return (
                            <Card key={person.id} className={`relative ${!person.is_active ? 'opacity-60' : ''}`}>
                                <CardHeader className="pb-3">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <CardTitle className="text-lg">{person.name}</CardTitle>
                                            <CardDescription className="text-sm">{person.email}</CardDescription>
                                        </div>
                                        <Badge variant={person.is_active ? "default" : "secondary"}>
                                            {person.is_active ? "Active" : "Inactive"}
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {person.phone && (
                                        <div className="text-sm text-muted-foreground">
                                            📞 {person.phone}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <p className="text-muted-foreground">Earned</p>
                                            <p className="font-semibold text-green-600">
                                                {formatPrice(person.total_commission_earned || 0)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Pending</p>
                                            <p className="font-semibold text-orange-600">
                                                {formatPrice(pending)}
                                            </p>
                                        </div>
                                    </div>

                                    {person.notes && (
                                        <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded whitespace-pre-wrap">
                                            {person.notes}
                                        </div>
                                    )}

                                    <div className="flex gap-2 pt-2 border-t">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1"
                                            onClick={() => handleEdit(person)}
                                        >
                                            <Edit className="h-3 w-3 mr-1" /> Edit
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => toggleStatus(person.id, person.is_active)}
                                        >
                                            {person.is_active ? "Deactivate" : "Activate"}
                                        </Button>
                                        {hasPermission('delete_salespeople') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                onClick={() => handleDelete(person.id)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
